import React, { useCallback, useContext, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Input,
  message,
  Popconfirm,
  Row,
  Select,
  Table,
} from 'antd';
import dayjs from 'dayjs';

import UserAvatar from '@totem/components/common/avatar/UserAvatar';
import DetailSpinner from '@totem/components/common/DetailSpinner';
import Multifactor from '@totem/components/settings/sections/profile/Multifactor';
import { RequestProfileUpdate } from '@totem/components/settings/sections/profile/types';
import IBTabs from '@totem/components/ui_components/antDesignOverrides/IBTabs';
import {
  mainMenuDisplayOptions,
  themeOptions,
} from '@totem/components/ui_components/menus/types';
import { PersistentData } from '@totem/components/userContext/persistence/types';
import UserPersistenceContext from '@totem/components/userContext/persistence/UserPersistenceContext';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import {
  userRoleString,
  userRoleStyle,
} from '@totem/components/userContext/security/userUtilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import colors from '@totem/styles/colors';
import { isNotNull } from '@totem/utilities/common';
import { USER_ENDPOINT, USERS_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';
import { sortStringAscending } from '@totem/utilities/tableUtilities';

import VersionsTile from '../../VersionsTile';

import ProfileTitleHeader from './ProfileTitleHeader';

import './profile.css';

const styles = {
  form: {
    width: '100%',
    paddingLeft: '2.75rem',
  },
  formItem: {
    paddingBottom: '0',
    marginBottom: '1rem',
  },
  avatar: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '3rem',
    height: '72px',
    width: '72px',
  },
  cardStyle: {
    width: '100%',
    color: colors.neutral.white,
  },
  cardHeader: {
    padding: '0',
  },
  cardBody: {
    padding: '1.5rem',
  },
};

interface EditableUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
}

export const Profile = () => {
  const {
    userProfile,
    getApiToken,
    mainMenuLocation,
    preferredTheme,
    refetch,
  } = useContext(UserProfileContext);
  const { persistenceData, persistenceLoaded, deleteComponentPersistence } =
    useContext(UserPersistenceContext);
  const [sending, setSending] = useState(false);

  const [user, setUser] = useState<EditableUser>({
    id: userProfile.id,
    email: userProfile.email,
    firstName: userProfile.firstName,
    lastName: userProfile.lastName,
    phoneNumber: userProfile.phoneNumber,
  });

  const [editMode, setEditMode] = useState<boolean>(false);

  const sendProfileUpdate = useCallback(
    (request: RequestProfileUpdate) => {
      if (!sending) {
        setSending(true);

        fetch(`${USERS_ENDPOINT}/${request.userId}`, {
          method: 'PUT',
          headers: new Headers({
            Authorization: `Bearer ${getApiToken()}`,
          }),
          body: JSON.stringify(request),
        }).then((res) => {
          setSending(false);

          CheckResponseShowError(res);

          setEditMode(false);
        });
      }
    },
    [getApiToken],
  );

  const sendMenuLocationPreference = useCallback(
    async (menuLocation: string) => {
      if (sending) {
        return;
      }

      fetch(`${USER_ENDPOINT}/preferences/menuLocation`, {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: menuLocation,
      })
        .then((response) => {
          if (response.status === 200) {
            refetch();
          } else {
            message.error('Home page update failed!');
          }
        })
        .then(() => {
          setSending(false);
        });

      setSending(true);
    },
    [sending],
  );

  const sendThemePreference = useCallback(
    async (themePreference: string) => {
      if (sending) {
        return;
      }

      fetch(`${USER_ENDPOINT}/preferences/theme`, {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: themePreference,
      })
        .then((response) => {
          if (response.status === 200) {
            refetch();
          } else {
            message.error('Home page update failed!');
          }
        })
        .then(() => {
          setSending(false);
        });

      setSending(true);
    },
    [sending],
  );

  const onProfileSave = () => {
    const payload: RequestProfileUpdate = {
      userId: user.id,
      lastName: user.lastName,
      firstName: user.firstName,
      phoneNumber: user.phoneNumber,
    };
    sendProfileUpdate(payload);
  };
  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setUser({ ...user, [name]: value });
  };

  const toggleEditMode = () => {
    if (editMode) {
      setUser({
        id: userProfile.id,
        email: userProfile.email,
        firstName: userProfile.firstName,
        lastName: userProfile.lastName,
        phoneNumber: userProfile.phoneNumber,
      });
    }

    setEditMode(!editMode);
  };

  const getProfileAttributeContent = () => {
    return editMode ? (
      <div styleName="attribute-list-container">
        <div styleName="attribute-list-row">
          <div styleName="attribute-label edit">Email Address:</div>
          <div styleName="attribute-value">
            <Input
              disabled
              autoComplete="off"
              value={user.email}
              name="email"
            />
          </div>
        </div>
        <div styleName="attribute-list-row">
          <div styleName="attribute-label edit">First Name:</div>
          <div styleName="attribute-value">
            <Input
              value={user.firstName}
              autoComplete="off"
              name="firstName"
              onChange={(event) => onChange(event)}
            />
          </div>
        </div>
        <div styleName="attribute-list-row">
          <div styleName="attribute-label edit">Last Name:</div>
          <div styleName="attribute-value">
            <Input
              value={user.lastName}
              autoComplete="off"
              name="lastName"
              onChange={(event) => onChange(event)}
            />
          </div>
        </div>
        <div styleName="attribute-list-row">
          <div styleName="attribute-label edit">Phone Number:</div>
          <div styleName="attribute-value">
            <Input
              value={user.phoneNumber}
              autoComplete="off"
              name="phoneNumber"
              onChange={(event) => onChange(event)}
            />
          </div>
        </div>
      </div>
    ) : (
      <div styleName="attribute-list-container">
        <div styleName="attribute-list-row">
          <div styleName="attribute-label">Email Address:</div>
          <div styleName="attribute-value">{userProfile.email || '-'}</div>
        </div>
        <div styleName="attribute-list-row">
          <div styleName="attribute-label">First Name:</div>
          <div styleName="attribute-value">{userProfile.firstName || '-'}</div>
        </div>
        <div styleName="attribute-list-row">
          <div styleName="attribute-label">Last Name:</div>
          <div styleName="attribute-value">{userProfile.lastName || '-'}</div>
        </div>
        <div styleName="attribute-list-row">
          <div styleName="attribute-label">Phone Number:</div>
          <div styleName="attribute-value">
            {userProfile.phoneNumber || '-'}
          </div>
        </div>
      </div>
    );
  };

  const roleString = userRoleString(userProfile.organization.role);
  const roleStyle = userRoleStyle(userProfile.organization.role);

  const content = sending ? (
    <div styleName="spinner-container">
      <DetailSpinner />
    </div>
  ) : (
    <div styleName="detail-container">
      <div styleName="profile-card">
        <Multifactor userID={userProfile.id} />
      </div>
      <div styleName="profile-card">
        <Card
          title={
            <ProfileTitleHeader
              isEdit={editMode}
              onSave={onProfileSave}
              loading={sending}
              toggleProfileEditing={toggleEditMode}
            />
          }
          style={styles.cardStyle}
          styles={{ body: styles.cardBody, header: styles.cardHeader }}
          variant="borderless"
        >
          {getProfileAttributeContent()}
        </Card>
      </div>
      <div styleName="profile-card">
        <Card
          title={
            <div styleName="settings-group-header-container">
              <div styleName="settings-group-header-label">Preferences</div>
            </div>
          }
          style={styles.cardStyle}
          styles={{ body: styles.cardBody, header: styles.cardHeader }}
          variant="borderless"
        >
          <Row>
            <Col span={12}>
              <span styleName={'security-group-value'}>Main Menu Position</span>
            </Col>
            <Col span={12}>
              <Select
                disabled={sending}
                style={{ width: '200px' }}
                onChange={(newValue: string) =>
                  sendMenuLocationPreference(newValue)
                }
                defaultValue={mainMenuLocation}
                value={mainMenuLocation}
                options={[
                  {
                    label: '-- Default --',
                    value: '',
                  },
                  ...mainMenuDisplayOptions.map((item) => {
                    return {
                      label: item,
                      value: item,
                    };
                  }),
                ]}
              />
            </Col>
          </Row>
          {permissions.internal.hasPermissions(userProfile) && (
            <Row>
              <Col span={12}>
                <span styleName={'security-group-value'}>
                  Theme (Experimental)
                </span>
              </Col>
              <Col span={12}>
                <Select
                  disabled={sending}
                  style={{ width: '200px' }}
                  onChange={(newValue: string) => sendThemePreference(newValue)}
                  defaultValue={preferredTheme}
                  value={preferredTheme}
                  options={[
                    ...themeOptions.map((item) => {
                      return {
                        label: item,
                        value: item,
                      };
                    }),
                  ]}
                />
              </Col>
            </Row>
          )}
        </Card>
      </div>
      <IBTabs
        items={[
          {
            key: 'securityGroups',
            label: 'Security Groups',
            children: (
              <Card
                title={
                  <div styleName="settings-group-header-container">
                    <div styleName="settings-group-header-label">
                      Security Groups
                    </div>
                  </div>
                }
                style={styles.cardStyle}
                styles={{ body: styles.cardBody, header: styles.cardHeader }}
                variant="borderless"
              >
                {isNotNull(userProfile.organization.securityGroups) &&
                  userProfile.organization.securityGroups.map((group) => {
                    return (
                      <Row key={group.id}>
                        <Col span={12}>
                          <span styleName={'security-group-value'}>
                            {group.name}
                          </span>
                        </Col>
                        <Col span={12}>
                          <span styleName={'security-group-value'}>
                            {group.description}
                          </span>
                        </Col>
                      </Row>
                    );
                  })}
              </Card>
            ),
          },
          {
            key: 'componentSettings',
            label: 'Component Saved Settings',
            children: (
              <Card
                title={
                  <div styleName="settings-group-header-container">
                    <div styleName="settings-group-header-label">
                      Saved View Settings
                    </div>
                  </div>
                }
                style={styles.cardStyle}
                styles={{ body: styles.cardBody, header: styles.cardHeader }}
                variant="borderless"
              >
                <Table
                  dataSource={persistenceData.sort((compA, compB) =>
                    sortStringAscending(compA.label, compB.label),
                  )}
                  rowKey="componentKey"
                  loading={!persistenceLoaded}
                  pagination={false}
                  size="small"
                  columns={[
                    {
                      title: 'Label',
                      dataIndex: 'label',
                      key: 'label',
                    },
                    {
                      title: 'Component Key',
                      dataIndex: 'componentKey',
                      key: 'componentKey',
                    },
                    {
                      title: '',
                      key: 'actions',
                      width: 80,
                      render: (_: unknown, record: PersistentData) => (
                        <Popconfirm
                          title="Delete saved settings for this component?"
                          onConfirm={() =>
                            deleteComponentPersistence(record.componentKey)
                          }
                          okText="Delete"
                          cancelText="Cancel"
                        >
                          <Button size="small" danger>
                            Delete
                          </Button>
                        </Popconfirm>
                      ),
                    },
                  ]}
                />
              </Card>
            ),
          },
        ]}
      />
    </div>
  );

  return (
    <div>
      <div styleName="profile-image-container">
        <div styleName="avatar">
          <UserAvatar user={userProfile} style={styles.avatar} />
        </div>
        <div styleName="profile-image-content">
          <div styleName="profile-image-name">
            {`${userProfile.firstName} ${userProfile.lastName}`}
          </div>
          <div style={roleStyle}>{roleString}</div>
          <div styleName="profile-image-date">
            {`Join Date: ${dayjs(userProfile.createdAt).format('MM/DD/YYYY')}`}
          </div>
        </div>
      </div>
      <div>{content}</div>
      <VersionsTile />
    </div>
  );
};
